import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { Client, LocalLabel, LabelUsage } from '../../models/client-management.model';
import { ClientUser, CreateClientUserRequest, LoginAttempt } from '../../models/client-user.model';

@Component({
  selector: 'app-clients-management',
  imports: [CommonModule, FormsModule],
  templateUrl: './clients-management.component.html',
  styleUrl: './clients-management.component.scss'
})
export class ClientsManagementComponent implements OnInit {
  clients: Client[] = [];
  loading = false;
  error: string | null = null;

  // Modal state
  showModal = false;
  isEditMode = false;
  currentClient: Partial<Client> = {};

  // Label management
  showLabelModal = false;
  selectedClientForLabels: Client | null = null;
  availableLabels: LocalLabel[] = [];
  clientLabels: string[] = [];

  // Autocomplete state
  labelSearchText = '';
  filteredLabels: LocalLabel[] = [];
  showSuggestions = false;

  // Labels section state
  showLabelsSection = false;
  allLabels: LocalLabel[] = [];
  labelsLoading = false;

  // Delete label confirmation
  showDeleteLabelModal = false;
  labelToDelete: LocalLabel | null = null;
  labelUsageInfo: LabelUsage | null = null;

  // User management
  showUsersModal = false;
  selectedClientForUsers: Client | null = null;
  clientUsers: ClientUser[] = [];
  usersLoading = false;
  showCreateUserModal = false;
  newUser: Partial<CreateClientUserRequest> = {};
  resendingUserId: number | null = null;

  // Login history
  showLoginHistoryModal = false;
  selectedUserForHistory: ClientUser | null = null;
  loginHistory: LoginAttempt[] = [];
  loginHistoryLoading = false;

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.loadClients();
  }

  loadClients(): void {
    this.loading = true;
    this.error = null;

    this.apiService.getClientsManagement().subscribe({
      next: (data) => {
        this.clients = data;
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load clients: ' + (err.error?.error || err.message);
        this.loading = false;
      }
    });
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.currentClient = {
      clientName: '',
      updateID: 1,
      description: '',
      isActive: true
    };
    this.showModal = true;
  }

  openEditModal(client: Client): void {
    this.isEditMode = true;
    this.currentClient = { ...client };
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
    this.currentClient = {};
  }

  saveClient(): void {
    if (!this.currentClient.clientName?.trim()) {
      alert('Client name is required');
      return;
    }

    this.loading = true;

    if (this.isEditMode && this.currentClient.clientParamID) {
      this.apiService.updateClient(this.currentClient.clientParamID, this.currentClient as Client).subscribe({
        next: () => {
          this.loadClients();
          this.closeModal();
        },
        error: (err) => {
          alert('Failed to update client: ' + (err.error?.error || err.message));
          this.loading = false;
        }
      });
    } else {
      this.apiService.createClient(this.currentClient).subscribe({
        next: () => {
          this.loadClients();
          this.closeModal();
        },
        error: (err) => {
          alert('Failed to create client: ' + (err.error?.error || err.message));
          this.loading = false;
        }
      });
    }
  }

  deleteClient(client: Client): void {
    if (!confirm(`Are you sure you want to delete client "${client.clientName}"?`)) {
      return;
    }

    this.loading = true;
    this.apiService.deleteClient(client.clientParamID).subscribe({
      next: () => {
        this.loadClients();
      },
      error: (err) => {
        alert('Failed to delete client: ' + (err.error?.error || err.message));
        this.loading = false;
      }
    });
  }

  openLabelModal(client: Client): void {
    this.selectedClientForLabels = client;
    this.clientLabels = [...(client.labels || [])];
    this.showLabelModal = true;
    this.labelSearchText = '';
    this.filteredLabels = [];
    this.showSuggestions = false;

    // Load available labels
    this.apiService.getLocalLabels().subscribe({
      next: (labels) => {
        this.availableLabels = labels;
        this.filterLabels();
      },
      error: (err) => {
        alert('Failed to load labels: ' + (err.error?.error || err.message));
      }
    });
  }

  closeLabelModal(): void {
    this.showLabelModal = false;
    this.selectedClientForLabels = null;
    this.clientLabels = [];
    this.availableLabels = [];
    this.labelSearchText = '';
    this.filteredLabels = [];
    this.showSuggestions = false;
  }

  onLabelSearchChange(): void {
    this.filterLabels();
    this.showSuggestions = true;
  }

  filterLabels(): void {
    const searchLower = this.labelSearchText.toLowerCase().trim();
    this.filteredLabels = this.availableLabels.filter(label =>
      !this.clientLabels.includes(label.labelDesc) &&
      label.labelDesc.toLowerCase().includes(searchLower)
    );
  }

  labelExistsInSuggestions(): boolean {
    const searchLower = this.labelSearchText.toLowerCase().trim();
    return this.availableLabels.some(l => l.labelDesc.toLowerCase() === searchLower);
  }

  selectLabel(label: LocalLabel): void {
    if (!this.selectedClientForLabels) return;

    this.apiService.assignLabelToClient(this.selectedClientForLabels.clientParamID, label.labelID).subscribe({
      next: () => {
        this.clientLabels.push(label.labelDesc);
        this.labelSearchText = '';
        this.filterLabels();
        this.showSuggestions = false;
        this.loadClients();
      },
      error: (err) => {
        alert('Failed to assign label: ' + (err.error?.error || err.message));
      }
    });
  }

  addNewLabel(event: Event): void {
    event.preventDefault();
    if (!this.selectedClientForLabels || !this.labelSearchText.trim()) return;

    const labelText = this.labelSearchText.trim();

    // Check if already assigned
    if (this.clientLabels.includes(labelText)) {
      this.labelSearchText = '';
      this.showSuggestions = false;
      return;
    }

    // Check if label exists in available labels
    const existingLabel = this.availableLabels.find(l =>
      l.labelDesc.toLowerCase() === labelText.toLowerCase()
    );

    if (existingLabel) {
      this.selectLabel(existingLabel);
    } else {
      // Create new label and assign it
      this.apiService.createLocalLabel(labelText).subscribe({
        next: (newLabel) => {
          this.availableLabels.push(newLabel);
          this.selectLabel(newLabel);
        },
        error: (err) => {
          alert('Failed to create label: ' + (err.error?.error || err.message));
        }
      });
    }
  }

  removeLabel(labelDesc: string): void {
    if (!this.selectedClientForLabels) return;

    const label = this.availableLabels.find(l => l.labelDesc === labelDesc);
    if (!label) return;

    this.apiService.removeLabelFromClient(this.selectedClientForLabels.clientParamID, label.labelID).subscribe({
      next: () => {
        this.clientLabels = this.clientLabels.filter(l => l !== labelDesc);
        this.filterLabels();
        this.loadClients();
      },
      error: (err) => {
        alert('Failed to remove label: ' + (err.error?.error || err.message));
      }
    });
  }

  // Labels section methods
  toggleLabelsSection(): void {
    this.showLabelsSection = !this.showLabelsSection;
    if (this.showLabelsSection && this.allLabels.length === 0) {
      this.loadAllLabels();
    }
  }

  loadAllLabels(): void {
    this.labelsLoading = true;
    this.apiService.getLocalLabels().subscribe({
      next: (labels) => {
        this.allLabels = labels;
        this.labelsLoading = false;
      },
      error: (err) => {
        alert('Failed to load labels: ' + (err.error?.error || err.message));
        this.labelsLoading = false;
      }
    });
  }

  confirmDeleteLabel(label: LocalLabel): void {
    this.labelToDelete = label;
    this.labelUsageInfo = null;
    this.showDeleteLabelModal = true;

    // Load usage info
    this.apiService.getLabelUsage(label.labelID).subscribe({
      next: (usage) => {
        this.labelUsageInfo = usage;
      },
      error: (err) => {
        console.error('Failed to load label usage:', err);
      }
    });
  }

  cancelDeleteLabel(): void {
    this.showDeleteLabelModal = false;
    this.labelToDelete = null;
    this.labelUsageInfo = null;
  }

  executeDeleteLabel(): void {
    if (!this.labelToDelete) return;

    this.apiService.deleteLocalLabel(this.labelToDelete.labelID).subscribe({
      next: () => {
        this.allLabels = this.allLabels.filter(l => l.labelID !== this.labelToDelete?.labelID);
        this.cancelDeleteLabel();
        this.loadClients(); // Refresh clients to update label displays
      },
      error: (err) => {
        alert('Failed to delete label: ' + (err.error?.error || err.message));
      }
    });
  }

  // User management methods
  openUsersModal(client: Client): void {
    this.selectedClientForUsers = client;
    this.showUsersModal = true;
    this.loadClientUsers(client.clientParamID);
  }

  closeUsersModal(): void {
    this.showUsersModal = false;
    this.selectedClientForUsers = null;
    this.clientUsers = [];
  }

  loadClientUsers(clientId: number): void {
    this.usersLoading = true;
    this.apiService.getClientUsersByClient(clientId).subscribe({
      next: (users) => {
        this.clientUsers = users;
        this.usersLoading = false;
      },
      error: (err) => {
        alert('Failed to load users: ' + (err.error?.error || err.message));
        this.usersLoading = false;
      }
    });
  }

  openCreateUserModal(): void {
    this.newUser = {
      clientParamID: this.selectedClientForUsers?.clientParamID,
      email: '',
      firstName: '',
      lastName: ''
    };
    this.showCreateUserModal = true;
  }

  closeCreateUserModal(): void {
    this.showCreateUserModal = false;
    this.newUser = {};
  }

  createUser(): void {
    if (!this.newUser.email) {
      alert('Email is required');
      return;
    }

    this.usersLoading = true;
    this.apiService.createClientUser(this.newUser as CreateClientUserRequest).subscribe({
      next: () => {
        this.closeCreateUserModal();
        if (this.selectedClientForUsers) {
          this.loadClientUsers(this.selectedClientForUsers.clientParamID);
        }
      },
      error: (err) => {
        alert('Failed to create user: ' + (err.error?.error || err.message));
        this.usersLoading = false;
      }
    });
  }

  deleteUser(user: ClientUser): void {
    if (!confirm(`Are you sure you want to delete user "${user.email}"?`)) {
      return;
    }

    this.usersLoading = true;
    this.apiService.deleteClientUser(user.clientUserID).subscribe({
      next: () => {
        if (this.selectedClientForUsers) {
          this.loadClientUsers(this.selectedClientForUsers.clientParamID);
        }
      },
      error: (err) => {
        alert('Failed to delete user: ' + (err.error?.error || err.message));
        this.usersLoading = false;
      }
    });
  }

  resendSetupEmail(user: ClientUser): void {
    this.resendingUserId = user.clientUserID;
    this.apiService.resendPasswordSetupEmail(user.clientUserID).subscribe({
      next: () => {
        alert(`Password setup email sent to ${user.email}`);
        this.resendingUserId = null;
      },
      error: (err) => {
        alert('Failed to send email: ' + (err.error?.error || err.message));
        this.resendingUserId = null;
      }
    });
  }

  // Login history methods
  openLoginHistory(user: ClientUser): void {
    this.selectedUserForHistory = user;
    this.showLoginHistoryModal = true;
    this.loadLoginHistory(user.clientUserID);
  }

  closeLoginHistoryModal(): void {
    this.showLoginHistoryModal = false;
    this.selectedUserForHistory = null;
    this.loginHistory = [];
  }

  loadLoginHistory(userId: number): void {
    this.loginHistoryLoading = true;
    this.apiService.getUserLoginHistory(userId).subscribe({
      next: (data) => {
        this.loginHistory = data;
        this.loginHistoryLoading = false;
      },
      error: (err) => {
        console.error('Error loading login history:', err);
        this.loginHistoryLoading = false;
      }
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Success': return 'status-success';
      case 'AwaitingOTP': return 'status-awaiting';
      case 'WrongPassword': return 'status-wrong-password';
      case 'WrongOTP': return 'status-wrong-otp';
      default: return '';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'Success': return 'Success';
      case 'AwaitingOTP': return 'Awaiting OTP';
      case 'WrongPassword': return 'Wrong Password';
      case 'WrongOTP': return 'Wrong OTP';
      default: return status;
    }
  }
}
